import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, X, ArrowUpDown } from 'lucide-react';

/** Barra filtri documenti: ricerca, tipo, cliente, operatore, range date, ordinamento. */
export default function DocumentiFilters({
  filtri, setFiltri, resetFiltri, hasActiveFiltri,
  tipiVisibili, clienti, utenti, documenti, isAdmin,
  sortBy, setSortBy, sortDir, setSortDir, risultati,
}) {
  const set = (k, v) => setFiltri(prev => ({ ...prev, [k]: v }));

  return (
    <Card className="rounded-md">
      <CardContent className="p-4 space-y-4">
        {/* Riga 1: Ricerca + Tipo */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per numero, oggetto, cliente..."
              value={filtri.search}
              onChange={(e) => set('search', e.target.value)}
              className="pl-10 rounded-md"
              data-testid="search-documenti"
            />
            {filtri.search && (
              <button
                onClick={() => set('search', '')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Select value={filtri.tipo || "all"} onValueChange={(v) => set('tipo', v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-44 rounded-md" data-testid="filter-tipo">
              <SelectValue placeholder="Tipo documento" />
            </SelectTrigger>
            <SelectContent className="rounded-md">
              <SelectItem value="all">Tutti i tipi</SelectItem>
              {tipiVisibili.includes('preventivo') && <SelectItem value="preventivo">Preventivo</SelectItem>}
              {tipiVisibili.includes('offerta') && <SelectItem value="offerta">Offerta</SelectItem>}
              {tipiVisibili.includes('ddt') && <SelectItem value="ddt">DDT</SelectItem>}
              {tipiVisibili.includes('fattura') && <SelectItem value="fattura">Fattura</SelectItem>}
              {tipiVisibili.includes('nota_credito') && <SelectItem value="nota_credito">Nota Credito</SelectItem>}
            </SelectContent>
          </Select>
          <Select value={filtri.stato || "all"} onValueChange={(v) => set('stato', v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-44 rounded-md" data-testid="filter-stato">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent className="rounded-md">
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="bozza">Bozza</SelectItem>
              <SelectItem value="in_attesa">In attesa</SelectItem>
              <SelectItem value="accettato">Accettato</SelectItem>
              <SelectItem value="rifiutato">Rifiutato</SelectItem>
              <SelectItem value="pagata">Fattura pagata</SelectItem>
              <SelectItem value="non_pagata">Fattura da incassare</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Riga 2: Filtri avanzati */}
        <div className="flex flex-wrap gap-3">
          <Select value={filtri.cliente || "all"} onValueChange={(v) => set('cliente', v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-48 rounded-md">
              <SelectValue placeholder="Tutti i clienti" />
            </SelectTrigger>
            <SelectContent className="rounded-md max-h-60">
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {clienti.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filtri.creatoDa || "all"} onValueChange={(v) => set('creatoDa', v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-52 rounded-md" data-testid="filter-creato-da">
              <SelectValue placeholder="Operatore/Tecnico" />
            </SelectTrigger>
            <SelectContent className="rounded-md max-h-60">
              <SelectItem value="all">Tutti gli operatori</SelectItem>
              {isAdmin && utenti.length > 0 ? (
                utenti.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                ))
              ) : (
                [...new Map(
                  documenti
                    .filter(d => d.created_by && d.created_by_nome)
                    .map(d => [d.created_by, { id: d.created_by, nome: d.created_by_nome }])
                ).values()].map((creator) => (
                  <SelectItem key={creator.id} value={creator.id}>{creator.nome}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Dal:</span>
            <Input type="date" value={filtri.dataDa} onChange={(e) => set('dataDa', e.target.value)} className="w-36 rounded-md h-9 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Al:</span>
            <Input type="date" value={filtri.dataA} onChange={(e) => set('dataA', e.target.value)} className="w-36 rounded-md h-9 text-sm" />
          </div>

          {hasActiveFiltri && (
            <Button variant="ghost" size="sm" onClick={resetFiltri} className="h-9 text-xs text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3 mr-1" />
              Reset filtri
            </Button>
          )}
        </div>

        {/* Indicatore filtri attivi + ordinamento */}
        {hasActiveFiltri && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Risultati: <strong className="text-foreground">{risultati}</strong> documenti</span>
            <div className="flex items-center gap-2 ml-auto">
              <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-8 w-40 rounded-md text-xs" data-testid="documenti-sort-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-md">
                  <SelectItem value="data">Data creazione</SelectItem>
                  <SelectItem value="numero">Numero documento</SelectItem>
                  <SelectItem value="totale">Importo</SelectItem>
                  <SelectItem value="cliente">Cliente (A-Z)</SelectItem>
                  <SelectItem value="stato">Stato accettazione</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-md text-xs px-2"
                onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')}
                title={sortDir === 'desc' ? 'Ordine decrescente' : 'Ordine crescente'}
                data-testid="documenti-sort-dir"
              >
                {sortDir === 'desc' ? '↓' : '↑'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
